"use client";

import { useFormStatus } from "react-dom";
import type { ButtonHTMLAttributes, ReactNode, Ref } from "react";

import {
  pillClass,
  pillContent,
  type PillSize,
  type PillVariant,
} from "./office/ui";
import { rainbowRingClass } from "./rainbow-ring";

/**
 * Submit button that shows the shared rainbow loading ring (globals.css) while
 * its form's action is in flight — the same treatment as the CSV export and fx
 * lookup. The label stays put rather than swapping to "Saving…".
 *
 * Pending comes from `useFormStatus` by default, which covers plain
 * `<form action={serverAction}>` submits (WriteSubmit, ConfirmButton, …). But
 * `useFormStatus` does NOT report pending for forms wired through
 * `useActionState`'s dispatcher, so those forms pass their `useActionState`
 * pending in via the explicit `busy` prop, which wins when provided.
 *
 * Pass `variant`/`size` for the house pill look (ui.tsx pillClass, the same
 * vocabulary as Button / WriteSubmit) instead of hand-rolled classes; `solid`
 * then follows the variant automatically (`variant="solid"` fades its fill out
 * while busy so the ring reads as the outline). Without a `variant`, `solid`
 * defaults true for the classic filled bg-fg/text-bg pill — pass
 * `solid={false}` for outline/text buttons. `disabled` is for *other* reasons
 * to block submit (can't-write, empty form); it greys the button, whereas the
 * in-flight disable does not (the ring is the cue). In a multi-submit form,
 * only the pressed button — its name/value present in the submitted FormData —
 * rings.
 */
export function SubmitButton({
  busy,
  variant,
  size = "md",
  solid,
  disabled = false,
  className = "",
  name,
  value,
  ref,
  children,
  ...rest
}: {
  /** Explicit in-flight flag for `useActionState` forms (see note above). When
   *  omitted, falls back to `useFormStatus` for plain server-action forms. */
  busy?: boolean;
  /** House pill look (ui.tsx pillClass, same vocabulary as Button) — pass
   *  this instead of hand-rolled classes for standard action buttons. */
  variant?: PillVariant;
  size?: PillSize;
  solid?: boolean;
  disabled?: boolean;
  /** The rendered <button> — e.g. the filter toolbar reads `.form` off it. */
  ref?: Ref<HTMLButtonElement>;
  children: ReactNode;
} & Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "type" | "disabled" | "children"
>) {
  const { pending, data } = useFormStatus();
  const isBusy =
    busy ?? (pending && (name == null || data?.get(name) === value));
  // With a variant, the busy fill-fade follows it (only `solid` fills);
  // without one, keep the legacy default (filled pill) unless overridden.
  const isSolid =
    solid ??
    (variant ? variant === "solid" || variant === "danger-solid" : true);
  const cls = variant
    ? `${pillClass(variant, size)} ${className}`.trim()
    : className;

  return (
    <button
      ref={ref}
      type="submit"
      name={name}
      value={value}
      aria-busy={isBusy || undefined}
      disabled={disabled || isBusy}
      className={`${rainbowRingClass({ solid: isSolid, busy: isBusy, disabled })} ${cls}`}
      {...rest}
    >
      {variant ? pillContent(size, children) : children}
    </button>
  );
}
